import React, { useState } from 'react';
import {
  Typography,
  Card,
  Form,
  Input,
  Button,
  Avatar,
  Row,
  Col,
  Descriptions,
  message,
  Divider,
  Space
} from 'antd';
import {
  UserOutlined,
  EditOutlined,
  SaveOutlined,
  MailOutlined,
  PhoneOutlined,
  TeamOutlined,
  IdcardOutlined
} from '@ant-design/icons';
import { useAppSelector } from '../store';

const { Title, Text } = Typography;

const Profile: React.FC = () => {
  const { user } = useAppSelector((state) => state.auth);
  const [isEditing, setIsEditing] = useState(false);
  const [form] = Form.useForm();

  const handleSubmit = (values: any) => {
    console.log('Profile updated:', values);
    message.success('个人资料更新成功');
    setIsEditing(false);
  };

  const getRoleName = (role?: string) => {
    switch (role) {
      case 'cdc':
        return '疾控中心管理员';
      case 'school':
        return '学校医务室人员';
      case 'community':
        return '社区检查点人员';
      default:
        return '未知角色';
    }
  };

  return (
    <div>
      <div className="page-header">
        <Title level={2}>个人资料</Title>
      </div>

      <div className="page-content">
        <Row gutter={24}>
          {/* 左侧：头像和基本信息 */}
          <Col xs={24} md={8}>
            <Card>
              <div style={{ textAlign: 'center', marginBottom: 24 }}>
                <Avatar
                  size={120}
                  icon={<UserOutlined />}
                  style={{
                    backgroundColor: '#1890ff',
                    marginBottom: 16
                  }}
                />
                <Title level={4} style={{ marginBottom: 4 }}>
                  {user?.name || '用户'}
                </Title>
                <Text type="secondary">{getRoleName(user?.role)}</Text>
              </div>

              <Divider />

              <Descriptions column={1} size="small">
                <Descriptions.Item label={<><TeamOutlined /> 所属机构</>}>
                  {user?.organization || '未设置'}
                </Descriptions.Item>
                <Descriptions.Item label={<><IdcardOutlined /> 用户名</>}>
                  {user?.username || '未设置'}
                </Descriptions.Item>
                <Descriptions.Item label={<><MailOutlined /> 邮箱</>}>
                  未设置
                </Descriptions.Item>
                <Descriptions.Item label={<><PhoneOutlined /> 电话</>}>
                  未设置
                </Descriptions.Item>
              </Descriptions>

              <Divider />

              <div style={{ textAlign: 'center' }}>
                <Space>
                  <Text type="secondary">账户状态：</Text>
                  <Text style={{ color: '#52c41a' }}>正常</Text>
                </Space>
              </div>
            </Card>
          </Col>

          {/* 右侧：编辑表单 */}
          <Col xs={24} md={16}>
            <Card
              title="详细信息"
              extra={
                !isEditing ? (
                  <Button
                    type="primary"
                    icon={<EditOutlined />}
                    onClick={() => setIsEditing(true)}
                  >
                    编辑
                  </Button>
                ) : null
              }
            >
              <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                initialValues={{
                  name: user?.name,
                  email: '',
                  phone: '',
                  organization: user?.organization,
                  department: '',
                  position: '',
                }}
                disabled={!isEditing}
              >
                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="name"
                      label="姓名"
                      rules={[{ required: true, message: '请输入姓名' }]}
                    >
                      <Input prefix={<UserOutlined />} placeholder="请输入姓名" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="phone"
                      label="联系电话"
                    >
                      <Input prefix={<PhoneOutlined />} placeholder="请输入联系电话" />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="email"
                      label="邮箱"
                      rules={[{ type: 'email', message: '请输入有效的邮箱地址' }]}
                    >
                      <Input prefix={<MailOutlined />} placeholder="请输入邮箱" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="organization"
                      label="所属机构"
                    >
                      <Input prefix={<TeamOutlined />} placeholder="请输入所属机构" disabled />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="department"
                      label="部门"
                    >
                      <Input placeholder="请输入部门" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="position"
                      label="职位"
                    >
                      <Input placeholder="请输入职位" />
                    </Form.Item>
                  </Col>
                </Row>

                {isEditing && (
                  <Form.Item style={{ marginBottom: 0, marginTop: 16 }}>
                    <Space>
                      <Button
                        type="primary"
                        htmlType="submit"
                        icon={<SaveOutlined />}
                      >
                        保存修改
                      </Button>
                      <Button onClick={() => {
                        setIsEditing(false);
                        form.resetFields();
                      }}>
                        取消
                      </Button>
                    </Space>
                  </Form.Item>
                )}
              </Form>
            </Card>

            {/* 账户安全卡片 */}
            <Card title="账户安全" style={{ marginTop: 24 }}>
              <Row gutter={[16, 16]}>
                <Col xs={24} md={12}>
                  <Card size="small" hoverable>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <Text strong>登录密码</Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          建议定期更换密码以保障账户安全
                        </Text>
                      </div>
                      <Button type="link">修改</Button>
                    </div>
                  </Card>
                </Col>
                <Col xs={24} md={12}>
                  <Card size="small" hoverable>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <Text strong>绑定手机</Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          未绑定手机号
                        </Text>
                      </div>
                      <Button type="link">绑定</Button>
                    </div>
                  </Card>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default Profile;
